import { useEffect, useState } from 'react';
import { db, reference, type LocalInvoice, type LocalMessage, type LocalPlace, type LocalStop } from '../lib/localdb.js';
import { amount, dateLabel, text } from '../lib/format.js';
import { Chip } from '../components/Controls.js';
import { tenantSlug } from '../lib/api.js';
import { captureLabel, type CaptureKind } from '../lib/stop.js';
import { formatPhone } from '@monrdv/shared';

/**
 * At the door.
 *
 * This is the screen that earns the app. Everything on it is something the technician would
 * otherwise have to telephone the office to learn, and every part of it is read from the phone
 * rather than fetched — the API could answer most of these questions, but not from a
 * mechanical room.
 *
 * Two distinctions are load-bearing and neither is decoration:
 *
 *   - **This home versus the building.** The history is per dwelling. "82 people at this
 *     address" on an apartment is worse than saying nothing, so the block gets its own short
 *     section and its own count.
 *   - **This job's money, not the books.** `billing.read.job`, and nothing wider. A technician
 *     may be asked at the door what is owed on the work they are doing; they may not browse
 *     what the business is owed.
 */

type AssetType = { id: string; key: string; name: unknown };
type Requirement = { id: string; name: unknown; equipmentNote: unknown; needsContact: boolean; isHeight: boolean; isConfinedSpace: boolean };
type Severity = { id: string; kind: string; name: unknown; notice: unknown };

export function AtTheDoor({
  stop,
  place,
  capabilities,
}: {
  stop: LocalStop;
  place: LocalPlace | null;
  capabilities: Set<string>;
}) {
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [invoices, setInvoices] = useState<LocalInvoice[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [severities, setSeverities] = useState<Severity[]>([]);
  const [building, setBuilding] = useState<{ customerCount: number; notes: string[] } | null>(null);
  const [captureKinds, setCaptureKinds] = useState<CaptureKind[]>([]);
  const [slug, setSlug] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const database = await db();
      const all = await database.getAll('messages');
      setMessages(
        all
          .filter((message) => message.customerId === stop.customer.id || message.jobId === stop.job.id)
          .sort((a, b) => b.sentAt.localeCompare(a.sentAt))
          .slice(0, 20),
      );
      setInvoices((await database.getAllFromIndex('invoices', 'byJob', stop.job.id)) ?? []);
      setAssetTypes(await reference<AssetType[]>('assetTypes', []));
      setRequirements(await reference<Requirement[]>('accessRequirements', []));
      setSeverities(await reference<Severity[]>('hazardSeverities', []));
      setCaptureKinds(await reference<CaptureKind[]>('captureKinds', []));
      setSlug(await tenantSlug());
      if (stop.address?.buildingKey) {
        const row = await database.get('buildings', stop.address.buildingKey);
        setBuilding(row ? { customerCount: row.customerCount, notes: row.notes } : null);
      }
    };
    void load();
  }, [stop.id, stop.customer.id, stop.job.id, stop.address?.buildingKey]);

  const owing = invoices.filter((invoice) => Number(invoice.balance) > 0);
  const typeName = (id: string | null) => (id ? text(assetTypes.find((type) => type.id === id)?.name) : '');
  const severityName = (id: string) => text(severities.find((severity) => severity.id === id)?.name) || 'Danger';

  return (
    <div className="door">
      {/* Safety first, literally: an open finding decides what comes out of the van. */}
      {place && place.hazards.length > 0 && (
        <section className="door__block door__block--hazard">
          <h3>Dangers ouverts ici</h3>
          <ul>
            {place.hazards.map((hazard) => (
              <li key={hazard.id}>
                <span className={`sev sev--${hazard.target}`}>{severityName(hazard.severityId)}</span>
                <p>{hazard.note}</p>
                <p className="meta">
                  {hazard.reportedBy ? `${hazard.reportedBy} · ` : ''}
                  {dateLabel(hazard.at)}
                  {hazard.acknowledgedAt ? ` · signé par ${hazard.acknowledgedName ?? 'le client'}` : ''}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        The equipment here, and how to reach it.
        This is what turns "been here before" into "bring the 6 m ladder, and the key is with
        the concierge".
      */}
      <section className="door__block">
        <h3>Équipement à cette adresse</h3>
        {!place?.assets.length && <p className="quiet">Aucun équipement enregistré. Scannez une plaque pour en créer un.</p>}
        <ul className="assets">
          {place?.assets.map((asset) => (
            <li key={asset.id}>
              <p className="assets__name">
                {typeName(asset.assetTypeId) || 'Équipement'}
                {asset.brand ? ` · ${asset.brand}` : ''}
                {asset.model ? ` ${asset.model}` : ''}
              </p>
              {asset.serial && <p className="assets__serial tnum">N° série {asset.serial}</p>}
              {asset.locationLabel && <p className="meta">{asset.locationLabel}</p>}
              {asset.requirements.length > 0 && (
                <div className="chips">
                  {asset.requirements.map((chip) => {
                    const requirement = requirements.find((candidate) => candidate.id === chip.requirementId);
                    if (!requirement) return null;
                    return (
                      <Chip key={chip.requirementId} tone={requirement.isHeight || requirement.isConfinedSpace ? 'warm' : 'neutral'}>
                        {text(requirement.name)}
                      </Chip>
                    );
                  })}
                </div>
              )}
            </li>
          ))}
        </ul>

        {place && place.access.length > 0 && (
          <div className="chips chips--place">
            {place.access.map((chip) => {
              const requirement = requirements.find((candidate) => candidate.id === chip.requirementId);
              if (!requirement) return null;
              return (
                <Chip key={chip.requirementId} tone={requirement.isHeight || requirement.isConfinedSpace ? 'warm' : 'neutral'}>
                  {text(requirement.name)}
                  {chip.contactName ? ` · ${chip.contactName}` : ''}
                  {chip.note ? ` · ${chip.note}` : ''}
                </Chip>
              );
            })}
          </div>
        )}
      </section>

      {/*
        The dwelling's history — the part the address identity work exists for. A technician
        arriving at a unit cleaned in 2023 for a previous owner should see that.
      */}
      <section className="door__block">
        <h3>Ce logement</h3>
        {!place?.history.length && <p className="quiet">Première visite à cette adresse.</p>}
        <ul className="history">
          {place?.history.map((visit) => (
            <li key={visit.appointmentId}>
              <span className="history__when tnum">{dateLabel(visit.at)}</span>
              <span className="history__what">
                {(visit.services as unknown[]).map((service) => text(service)).join(' · ') || visit.jobNumber}
              </span>
              <span className="history__who">{visit.customerName}</span>
              {visit.captureCount > 0 && <span className="history__count tnum">{visit.captureCount} 📷</span>}
            </li>
          ))}
        </ul>

        {/*
          The evidence behind "been here before".
          The images are fetched, not held: a thumbnail seen in the street is cached by the
          service worker and stays viewable in the basement, and one that was never opened
          shows as its kind — which is still more than an untyped pile ever said.
        */}
        {place && place.captures.length > 0 && (
          <ul className="gallery">
            {place.captures.slice(0, 12).map((capture) => (
              <li key={capture.id}>
                {slug ? (
                  <img
                    src={`/api/${slug}/media/${capture.id}`}
                    alt={captureLabel(capture.kind, captureKinds)}
                    loading="lazy"
                  />
                ) : (
                  <span className="gallery__kind">{captureLabel(capture.kind, captureKinds)}</span>
                )}
                <span className="gallery__label">
                  {captureLabel(capture.kind, captureKinds)}
                  {capture.capturedAt ? ` · ${dateLabel(capture.capturedAt)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}

        {building && building.customerCount > 1 && (
          <p className="quiet">
            {/* Never presented as one household. The largest block here holds 82 customers. */}
            Immeuble de {building.customerCount} adresses.
            {building.notes.length > 0 && ` ${building.notes.join(' · ')}`}
          </p>
        )}
      </section>

      {/* On a commercial site the person at the door is often not the person who pays. */}
      {(stop.siteContactName || stop.poNumber || stop.customer.parentId) && (
        <section className="door__block">
          <h3>Site commercial</h3>
          {stop.siteContactName && (
            <p>
              Contact sur place : {stop.siteContactName}
              {stop.siteContactPhone ? ` · ${formatPhone(stop.siteContactPhone)}` : ''}
            </p>
          )}
          {stop.poNumber ? (
            <p className="tnum">Bon de commande {stop.poNumber}</p>
          ) : (
            <p className="quiet">Aucun bon de commande. Une facture commerciale sans numéro n’est pas payée.</p>
          )}
        </section>
      )}

      {/* This job's money, and only this job's. */}
      {capabilities.has('billing.read.job') && (
        <section className="door__block">
          <h3>Ce qui est dû sur ce travail</h3>
          {!owing.length && <p className="quiet">Rien à percevoir.</p>}
          <ul className="owing">
            {owing.map((invoice) => (
              <li key={invoice.id}>
                <span className="tnum">{invoice.number}</span>
                <span className="tnum">{amount(invoice.balance)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/*
        One thread per customer, all channels. A technician who can read that the office
        promised a 9 am arrival does not get shouted at for it.
      */}
      <section className="door__block">
        <h3>Conversation</h3>
        {!messages.length && <p className="quiet">Aucun message récent.</p>}
        <ul className="thread">
          {messages.map((message) => (
            <li key={message.id} className={`thread__${message.authorKind}`}>
              <p className="meta">
                {message.authorName ?? (message.authorKind === 'customer' ? 'Client' : 'Bureau')} ·{' '}
                {dateLabel(message.sentAt)}
                {message.pending ? ' · non envoyé' : ''}
              </p>
              <p>{message.body}</p>
            </li>
          ))}
        </ul>
      </section>

      {stop.job.internalNote && (
        <section className="door__block">
          <h3>Consignes</h3>
          {/* Work instructions for the crew. Never shown to the customer. */}
          <p>{stop.job.internalNote}</p>
        </section>
      )}
    </div>
  );
}
